<script setup lang="ts">
  // V2 card-grid Printers tab (native): merged card model (discovered usb/bt
  // ∪ saved network ∪ assignment ghosts) from useNativePrintingUi, wizard
  // for adding, edit modal, honest per-card tests. Discovery + probes kick
  // from the modal SHELL on open (all tabs need them; jobs deep-link skips
  // this tab entirely) — only the explicit "Actualizar" button reloads
  // interactively (BT permission prompt allowed on that gesture only).
  import { computed, getCurrentInstance } from 'vue';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import { AlertType } from '@/interfaces/IAlert';
  import {
    INativePrinterCard,
    TNativeHelpFlow,
    useNativePrintingUi,
  } from '@/composition/printing/use-native-printing-ui';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import V2NativePrinterCard from './V2NativePrinterCard.vue';
  import V2NativePrinterEditModal from './V2NativePrinterEditModal.vue';
  import V2NativePrinterSetupWizard from './wizard/V2NativePrinterSetupWizard.vue';

  const emit = defineEmits<{
    (e: 'open-help', flow: TNativeHelpFlow): void;
  }>();

  const ui = useUi();
  const modals = useModal();
  const nativeUi = useNativePrintingUi();
  const tests = nativeUi.tests;

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const cards = computed(() => nativeUi.printerCards.value);

  /** Some saved network printer probed offline → deep-link into Help. */
  const showOfflineHint = computed(() =>
    nativeUi.networkPrinters.value.some(
      (np) => nativeUi.probeStateByName.value[np.name] === 'offline'
    )
  );

  function refresh() {
    void nativeUi.reload(true);
    nativeUi.probeAll();
  }

  // #region wizard / edit

  function openWizard() {
    modals.show(
      V2NativePrinterSetupWizard,
      {},
      {
        adaptive: true,
        height: 'auto',
        width: 720,
        maxWidth: window.innerWidth * 0.95,
        scrollable: true,
        name: 'v2-native-printer-setup-wizard',
      },
      {}
    );
  }

  function openEdit(card: INativePrinterCard) {
    modals.show(
      V2NativePrinterEditModal,
      { printerName: card.name },
      {
        adaptive: true,
        height: 'auto',
        width: 560,
        maxWidth: window.innerWidth * 0.95,
        scrollable: true,
        name: 'v2-native-printer-edit-modal',
      },
      {}
    );
  }

  // #endregion

  // #region tests (honest dispatch — kindOverride comes from the card's
  // explicit two-option menu when the printer is unassigned/ambiguous)

  function isTesting(card: INativePrinterCard): boolean {
    return (
      !!card.printer && tests.testingPrinterId.value === card.printer.id
    );
  }

  const anyTesting = computed(() => tests.testingPrinterId.value !== null);

  async function testCard(
    card: INativePrinterCard,
    kindOverride?: 'escpos' | 'zpl'
  ) {
    if (!card.printer) return;
    // Network preflight runs INSIDE the test guard: the whole flow (incl.
    // the up-to-4s probe) shows the card spinner and blocks concurrent
    // tests. Probar is also a supportsDleEot re-learn path.
    const networkConfig = card.networkConfig;
    const preflight = networkConfig
      ? async () => {
          const reachable = await nativeUi.probeOne(networkConfig);
          if (!reachable) {
            ui.showToast(
              AlertType.Error,
              i18nT('printingV2.nativeUnreachable')
            );
          }
          return reachable;
        }
      : undefined;
    await nativeUi.runPrinterTest(card.printer, kindOverride, preflight);
  }

  // #endregion

  // #region remove / unassign

  async function removeCard(card: INativePrinterCard) {
    if (!card.networkConfig) return;
    const accepted = await modals.confirm({
      title: i18nT('printingV2.nativeDeleteTitle'),
      message: i18nT('printingV2.nativeDeleteBody', { name: card.name }),
      acceptText: i18nT('printingV2.nativeDelete'),
    });
    if (!accepted) return;

    nativeUi.store.removeNetworkPrinter(card.name);
    nativeUi.removeProbeState(card.name);
  }

  /**
   * Ghost card action: durably clears every destination the ghost name
   * holds via clearAssignment (set*Printer(null) would resurrect from the
   * persisted name on the next discovery pass).
   */
  function unassignGhost(card: INativePrinterCard) {
    for (const assignment of card.assignments) {
      nativeUi.store.clearAssignment(
        assignment.destination,
        assignment.stationId
      );
    }
    ui.showToast(AlertType.Success, i18nT('printingV2.saved'));
  }

  // #endregion
</script>

<template>
  <div class="v2-native-printers-tab">
    <div v-if="showOfflineHint" class="printers-offline-hint">
      <PosUiIcon name="lan-disconnect" class="printers-offline-hint-icon" />
      <span class="printers-offline-hint-text">
        {{ $t('printingV2.nativeOfflineHint') }}
      </span>
      <button
        class="printers-offline-hint-link"
        type="button"
        @click="emit('open-help', 'noprint')"
      >
        {{ $t('printingV2.offlineHelpLink') }}
      </button>
    </div>

    <PosUiEmptyState
      v-if="!cards.length"
      icon="printer-off"
      :title="i18nT('printingV2.noPrintersTitle')"
      :description="i18nT('printingV2.noPrintersDescription')"
    >
      <template #action>
        <PosUiButton color="primary" icon="plus" @click="openWizard">
          {{ $t('printingV2.addPrinter') }}
        </PosUiButton>
      </template>
    </PosUiEmptyState>

    <template v-else>
      <div class="printers-grid">
        <V2NativePrinterCard
          v-for="card in cards"
          :key="card.key"
          :card="card"
          :test-kind="
            card.printer
              ? nativeUi.resolveTestKind(card.printer)
              : 'ambiguous'
          "
          :testing="isTesting(card)"
          :any-testing="anyTesting"
          @test="testCard(card, $event)"
          @edit="openEdit(card)"
          @remove="removeCard(card)"
          @unassign="unassignGhost(card)"
        />
      </div>

      <div class="printers-actions">
        <PosUiButton color="primary" icon="plus" @click="openWizard">
          {{ $t('printingV2.addPrinter') }}
        </PosUiButton>
        <PosUiButton
          color="neutral"
          variant="outline"
          icon="reload"
          :loading="nativeUi.reloading.value"
          @click="refresh"
        >
          {{ $t('printingV2.refresh') }}
        </PosUiButton>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
  .v2-native-printers-tab {
    @apply tw-flex tw-flex-col tw-gap-3;

    .printers-offline-hint {
      @apply tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-bg-neutral-100 tw-px-3 tw-py-2.5 tw-text-sm tw-text-neutral-600;

      .printers-offline-hint-icon {
        @apply tw-flex-none tw-text-lg tw-text-neutral-400;
      }

      .printers-offline-hint-text {
        @apply tw-flex-1;
      }

      .printers-offline-hint-link {
        @apply tw-flex-none tw-whitespace-nowrap tw-font-semibold tw-text-neutral-700 tw-underline tw-underline-offset-2 tw-transition-colors hover:tw-text-neutral-900;
      }
    }

    .printers-grid {
      @apply tw-grid tw-grid-cols-1 tw-gap-3 sm:tw-grid-cols-2;
    }

    .printers-actions {
      @apply tw-flex tw-justify-center tw-gap-2 tw-pt-1;
    }
  }
</style>
